import moment from "moment";

const TERMINATED_STATE = 5;
const OPEN_STATE = 2;

export const HISTORY_FIELD_DEFS = [
  { key: "id", labelKey: "options_list_id", labelDefault: "ID" },
  { key: "lot_size", labelKey: "options_list_lot_size", labelDefault: "Koko", type: "number" },
  { key: "lot_amount", labelKey: "options_list_lot_amount", labelDefault: "Määrä", type: "number" },
  { key: "quantity", labelKey: "options_list_quantity", labelDefault: "Kokonais-määrä", type: "number" },
  { key: "premium", labelKey: "options_list_premium", labelDefault: "Preemio / kpl", type: "money" },
  { key: "premium_total", labelKey: "options_list_premium_total", labelDefault: "Pree. yht", type: "money" },
  { key: "other_expenses", labelKey: "options_list_other_expenses", labelDefault: "Muut kulut", type: "money" },
  { key: "option_state_date", labelKey: "options_list_option_state_date", labelDefault: "Tilan pvm", type: "date" },
  { key: "termination_fee", labelKey: "options_list_termination_price", labelDefault: "Lopetus-hinta", type: "money" },
];

const toTimestamp = (date) => {
  if (!date) return 0;
  const parsed = moment(date);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

const normalizeFieldValue = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "number") return String(value);
  return String(value).trim();
};

const sumNumericField = (members, field) =>
  members.reduce((sum, member) => sum + (Number(member?.[field]) || 0), 0);

const buildOptionsById = (allOptions) =>
  new Map(
    (Array.isArray(allOptions) ? allOptions : [])
      .filter((row) => row?.id != null)
      .map((row) => [Number(row.id), row])
  );

/**
 * Walk up the parent_option_id chain to find the root (no parent).
 */
export const findRootOption = (option, optionsById) => {
  let current = option;
  const visited = new Set();
  while (
    current?.parent_option_id &&
    optionsById.has(Number(current.parent_option_id)) &&
    !visited.has(Number(current.parent_option_id))
  ) {
    visited.add(Number(current.id));
    current = optionsById.get(Number(current.parent_option_id));
  }
  return current;
};

/**
 * Collect all descendant IDs (transitively) that have parent_option_id pointing
 * into the already-collected set.
 */
export const collectFamilyIds = (rootId, optionsById) => {
  const ids = new Set([Number(rootId)]);
  let changed = true;
  while (changed) {
    changed = false;
    optionsById.forEach((option, id) => {
      const parentId = Number(option?.parent_option_id);
      if (parentId && ids.has(parentId) && !ids.has(Number(id))) {
        ids.add(Number(id));
        changed = true;
      }
    });
  }
  return ids;
};

export const getOptionFamily = (option, allOptions = []) => {
  const optionsById = buildOptionsById(allOptions);
  const currentOption = optionsById.get(Number(option?.id)) || option;
  const rootOption = findRootOption(currentOption, optionsById);
  if (!rootOption?.id) {
    return { rootOption: null, familyMembers: [], optionsById };
  }

  const familyIds = collectFamilyIds(rootOption.id, optionsById);
  const familyMembers = [...familyIds]
    .map((id) => optionsById.get(id))
    .filter(Boolean);

  return { rootOption, familyMembers, optionsById };
};

export const isTerminationChildOption = (option) => Boolean(option?.parent_option_id);

/**
 * Linear chain from root → … → tip following parent_option_id on each member.
 */
export const buildTerminationChain = (option, optionsById) => {
  const chain = [];
  let current = optionsById.get(Number(option?.id)) || option;
  const visited = new Set();

  while (current?.id != null && !visited.has(Number(current.id))) {
    visited.add(Number(current.id));
    chain.unshift(current);
    const parentId = Number(current?.parent_option_id);
    current =
      parentId && optionsById.has(parentId) ? optionsById.get(parentId) : null;
  }

  return chain;
};

const groupFamilies = (allOptions) => {
  const safe = Array.isArray(allOptions) ? allOptions : [];
  const optionsById = buildOptionsById(safe);
  const families = new Map();

  safe.forEach((option) => {
    if (option?.id == null) return;
    const root = findRootOption(option, optionsById);
    const rootId = Number(root?.id ?? option.id);
    if (!families.has(rootId)) families.set(rootId, []);
    families.get(rootId).push(option);
  });

  return { safe, families };
};

const pickFamilyDisplayMembers = (members) => {
  const openMembers = members.filter((member) => Number(member?.option_state) === OPEN_STATE);
  if (openMembers.length > 0) return openMembers;
  const tip = members.reduce((best, member) => (
    Number(member?.id) > Number(best?.id) ? member : best
  ));
  return tip ? [tip] : [];
};

/**
 * One list row per open contract in a termination family. Ancestors and
 * terminated siblings stay in history (95 → 97 → 98 ⇒ row 98).
 */
export const getListOptionsForDisplay = (allOptions = []) => {
  const { safe, families } = groupFamilies(allOptions);
  const displayIds = new Set();

  families.forEach((members) => {
    pickFamilyDisplayMembers(members).forEach((member) => {
      displayIds.add(Number(member.id));
    });
  });

  return safe.filter((option) => displayIds.has(Number(option?.id)));
};

/** @deprecated use getListOptionsForDisplay */
export const getRootOptionsForList = getListOptionsForDisplay;

const buildOriginalBaseline = (rootOption, familyMembers) => ({
  ...rootOption,
  id: rootOption.id,
  quantity: sumNumericField(familyMembers, "quantity"),
  lot_amount: sumNumericField(familyMembers, "lot_amount"),
  premium_total: sumNumericField(familyMembers, "premium_total"),
  other_expenses: sumNumericField(familyMembers, "other_expenses"),
  option_state: OPEN_STATE,
  termination_fee: rootOption?.termination_fee ?? 0,
});

/**
 * List row is already the correct contract (open child after partial termination).
 */
export const getOptionListDisplayRow = (option) => option;

export const getChangedFields = (baseline, contract, { alwaysIncludeId = false } = {}) =>
  HISTORY_FIELD_DEFS.filter((field) => {
    if (field.key === "id") {
      return alwaysIncludeId || Number(contract?.id) !== Number(baseline?.id);
    }
    return (
      normalizeFieldValue(baseline?.[field.key]) !== normalizeFieldValue(contract?.[field.key])
    );
  }).map((field) => ({
    ...field,
    value: contract?.[field.key],
  })).filter((field, _index, fields) => {
    if (field.key !== "lot_amount") {
      return true;
    }
    const quantityField = fields.find((item) => item.key === "quantity");
    return !quantityField || String(quantityField.value) !== String(field.value);
  });

const buildHistoryEventFields = (baseline, contract, { includeId = false } = {}) => {
  const fields = getChangedFields(baseline, contract, { alwaysIncludeId: includeId }).filter(
    (field) => field.key !== "id"
  );

  if (includeId) {
    fields.unshift({
      key: "id",
      labelKey: "options_list_id",
      labelDefault: "ID",
      value: contract?.id,
    });
  }

  return fields;
};

const dedupeTimeline = (events) => {
  const seen = new Set();
  return events.filter((event) => {
    const key = `${event.type}-${event.optionId ?? ""}-${event.date ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Build termination history for the list row. Walks the full parent chain so
 * every step (95 → 97 → 98) appears; newest event first, oldest numbered 1.
 */
export const buildOptionHistoryEvents = (option, allOptions = []) => {
  const optionsById = buildOptionsById(allOptions);
  const chain = buildTerminationChain(option, optionsById);
  if (chain.length <= 1) return [];

  const rootOption = chain[0];
  const { familyMembers } = getOptionFamily(option, allOptions);
  const baseline = buildOriginalBaseline(rootOption, familyMembers);
  const timeline = [];

  chain.forEach((member, index) => {
    const isTip = index === chain.length - 1;
    const parent = index > 0 ? chain[index - 1] : null;
    const parentTerminationTs = toTimestamp(
      parent?.option_state_date || parent?.start_date
    );
    const isOpen = Number(member.option_state) === OPEN_STATE;

    // Only the current tip is shown as "Uusi sopimus" — intermediates appear once as terminated.
    if (isTip && isOpen) {
      timeline.push({
        type: "new_contract",
        labelKey: "options_history_new_contract",
        labelDefault: "Uusi sopimus (avoin)",
        date: member.start_date,
        sortDate: parentTerminationTs
          ? new Date(parentTerminationTs + 1).toISOString()
          : member.start_date,
        optionId: member.id,
        contract: member,
        changedFields: buildHistoryEventFields(baseline, member, { includeId: true }),
      });
    }

    if (Number(member.option_state) === TERMINATED_STATE) {
      const terminatedDate = member.option_state_date || member.start_date;

      timeline.push({
        type: "terminated",
        labelKey: "options_history_terminated",
        labelDefault: "Terminoitu",
        date: terminatedDate,
        sortDate: terminatedDate,
        optionId: member.id,
        contract: member,
        changedFields: buildHistoryEventFields(baseline, member, { includeId: true }),
      });
    }
  });

  return dedupeTimeline(timeline).sort((a, b) => {
    const dateDiff = toTimestamp(b.sortDate || b.date) - toTimestamp(a.sortDate || a.date);
    if (dateDiff !== 0) return dateDiff;
    return Number(b.optionId) - Number(a.optionId);
  });
};

export const hasOptionHistory = (option, allOptions = []) => {
  const { familyMembers } = getOptionFamily(option, allOptions);
  return familyMembers.length > 1;
};

/**
 * Other contracts in the same termination family, newest first.
 * The current list row is omitted so opening history cannot repeat it.
 */
export const getListRowHistoryContracts = (option, allOptions = []) => {
  const { familyMembers } = getOptionFamily(option, allOptions);
  const currentId = Number(option?.id);
  const seen = new Set();

  return familyMembers
    .filter((member) => {
      const memberId = Number(member?.id);
      if (!memberId || memberId === currentId || seen.has(memberId)) return false;
      seen.add(memberId);
      return true;
    })
    .sort((left, right) => {
      const dateDiff = toTimestamp(right?.option_state_date || right?.start_date)
        - toTimestamp(left?.option_state_date || left?.start_date);
      if (dateDiff !== 0) return dateDiff;
      return Number(right?.id) - Number(left?.id);
    });
};
